import { observer } from 'mobx-react-lite'
import { type ReactElement, type ReactNode, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { ModalBackTitle } from 'app/components/ModalBackTitle'
import { ListSlotCard } from 'app/components/lists/board/ListSlotCard'
import { openListRuleEditor } from 'app/components/lists/filter/openListRuleEditor'
import { ListVisibilityField } from 'app/components/lists/ListVisibilityField'
import { useStageVisibility } from 'app/components/lists/manual/useStageVisibility'
import { EditStages } from 'app/components/pipelines/EditStages'
import { PipelineCardCustomization } from 'app/components/pipelines/PipelineCardCustomization'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { Switch } from 'app/components/ui/switch'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useSettings } from 'app/hooks/useSettings'
import { useSystemList } from 'app/hooks/useSystemList'
import { HIDE_TABLE_COLUMN_LINES_SETTING } from 'app/hooks/useTableColumnLines'
import { closeModal, setModalTitle, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { List } from 'app/generated/graphql'
import { useListStore, useUserSettingStore } from 'app/stores/StoreContext'
import type { ListSettingsTarget } from 'app/types/list-settings'
import type { ListVisibility } from 'app/types/list-visibility'
import type { SystemListDef } from 'app/types/navigation'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { getListCardConfig, LIST_CARD_CONFIG_SETTING, type ListCardConfig } from 'lib/listCardConfig'
import { isFieldEligible } from 'lib/listCardFields'
import { listSettingsRows } from 'lib/listSettingsRows'
import {
  canChangeVisibility,
  canDeleteList,
  isPersonalList,
  PERSONAL_LIST_NOTE,
  SHARED_LIST_NOTE,
} from 'lib/listVisibility'
import { logError } from 'lib/logging'

const MODAL_OPTIONS = { isFullScreen: false, removePadding: true, useRadix: true } as const

// A definite width, not a cap: the panel otherwise sizes to its content, which at 375px runs off both edges.
// Customize cards opens with MODAL_OPTIONS and keeps its content-sized panel.
const SETTINGS_MODAL_OPTIONS = { ...MODAL_OPTIONS, panelClassName: `min-w-0 w-full sm:w-120` } as const

const UNTITLED = `Untitled list`

type ListSettingsProps = {
  target: ListSettingsTarget
  /** The list was renamed, restaged or deleted: refetch whatever shows it. */
  onChanged: () => void
  /** The list no longer exists: leave its page. */
  onDeleted: () => void
  /** Show the list's table and open its own column editor, whose Back calls `backToSettings`. */
  onEditColumns: (backToSettings: () => void) => void
}

/**
 * One group of rows under a small label. Only groups are ruled off from each other, so a label
 * never sits boxed between two lines; the rows inside are divided more lightly.
 */
function SettingsSection({ title, children }: { title?: string; children: ReactNode }): ReactElement {
  return (
    <section className="border-t border-default px-6 pb-2 pt-4 first:border-t-0">
      {title && <h3 className="text-label-xs uppercase tracking-wide text-subtle">{title}</h3>}
      <div className="divide-y divide-subtle">{children}</div>
    </section>
  )
}

function SettingsRow({ title, help, children }: { title: string; help?: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center justify-between gap-6 py-3">
      <div className="min-w-0">
        <p className="text-ui-sm font-medium text-strong">{title}</p>
        {help && <p className="mt-0.5 text-body-xs text-subtle">{help}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  )
}

export function ListSettingsDialog(props: ListSettingsProps): ReactElement {
  const { target, onChanged, onDeleted, onEditColumns } = props
  const rows = listSettingsRows(target)
  const viewSections = (
    <>
      {target.source === `system` ? (
        rows.stages && <SystemStagesRow def={target.def} />
      ) : (
        <SettingsSection title="Board">
          <StagesRow list={target.list} />
          <CardsRow />
        </SettingsSection>
      )}
      <SettingsSection title="Table">
        {/* The table's own toolbar has no column button on a list page: this row is the way in. */}
        <SettingsRow title="Columns" help="Which columns this list's table shows, and in what order.">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => {
              closeModal()
              // The column editor replaces this dialog, so its Back opens Settings afresh.
              onEditColumns(() => openListSettings(props))
            }}
          >
            Edit columns
          </Button>
        </SettingsRow>
        <ColumnLinesRow />
      </SettingsSection>
    </>
  )
  return (
    <div className="flex w-full flex-col pb-2">
      {target.source === `practice` && rows.listSection ? (
        <ListSection
          list={target.list}
          editRule={rows.editRule}
          onChanged={onChanged}
          onDeleted={onDeleted}
          reopen={() => openListSettings(props)}
        >
          {viewSections}
        </ListSection>
      ) : (
        viewSections
      )}
    </div>
  )
}

/**
 * Name, who can see it and the rule first; `children` (the board and table sections) next; and
 * delete last, on its own, where a destructive action belongs. One component, since who may
 * delete follows the visibility picked above it.
 */
function ListSection({
  list,
  editRule,
  onChanged,
  onDeleted,
  reopen,
  children,
}: {
  list: List
  editRule: boolean
  onChanged: () => void
  onDeleted: () => void
  /** The rule editor replaces this dialog, so its Back opens Settings afresh. */
  reopen: () => void
  children: ReactNode
}): ReactElement {
  // The page and the sidebar read the List store, whose own read is cache-first, so it is read
  // again from the network after each write.
  const listStore = useListStore()
  const [confirm] = useConfirmation()
  const [name, setName] = useState(list.name)
  // What the list is actually called right now, which the field is free to differ from while
  // somebody is typing: the modal's own title and the delete message both speak for the record.
  const [savedName, setSavedName] = useState(list.name || UNTITLED)
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const user = useUser()
  const checkPermission = useCheckPermission()
  const [visibility, setVisibility] = useState<ListVisibility>(isPersonalList(list) ? `personal` : `shared`)
  const [savingVisibility, setSavingVisibility] = useState(false)

  const saveName = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setNameError(`Give the list a name`)
      return
    }
    setSaving(true)
    const result = await listStore.update(list.id, { name: trimmed })
    setSaving(false)
    if (result.error) {
      logError(`Error renaming a list`, result.error.message)
      notifyError(`That name could not be saved. ${result.error.message}`)
      return
    }
    await listStore.fetchList(undefined, { requestPolicy: `network-only` })
    setName(trimmed)
    setSavedName(trimmed)
    setModalTitle(trimmed)
    notifySuccess(`List renamed`)
    onChanged()
  }

  // Saved the moment it is picked, like the stages below it; a save that fails puts the old choice back.
  const saveWhoCanSee = async (next: ListVisibility) => {
    const previous = visibility
    setVisibility(next)
    setSavingVisibility(true)
    const result = await listStore.update(list.id, { visibility: next })
    setSavingVisibility(false)
    if (result.error) {
      logError(`Error changing who can see a list`, result.error.message)
      notifyError(`That change could not be saved. ${result.error.message}`)
      setVisibility(previous)
      return
    }
    await listStore.fetchList(undefined, { requestPolicy: `network-only` })
    notifySuccess(next === `personal` ? PERSONAL_LIST_NOTE : SHARED_LIST_NOTE)
    onChanged()
  }

  // The confirmation never resolves unless the person confirms, so everything below it is the
  // confirmed path.
  const deleteList = async () => {
    await confirm({
      title: `Delete list`,
      description: `People in this list are not deleted. They just stop being in it.`,
      variant: `danger`,
    })
    setDeleting(true)
    const result = await listStore.remove(list.id)
    if (result.error) {
      logError(`Error deleting a list`, result.error.message)
      notifyError(`That list could not be deleted. ${result.error.message}`)
      setDeleting(false)
      return
    }
    notifySuccess(`${savedName} deleted`)
    await listStore.fetchList(undefined, { requestPolicy: `network-only` })
    closeModal()
    onChanged()
    onDeleted()
  }

  const nameChanged = name.trim() !== savedName
  const canDelete = canDeleteList({ ...list, visibility }, user?.id, checkPermission(`lists:delete-shared`))

  return (
    <>
      <SettingsSection title="List">
        <div className="flex flex-col gap-2 py-3">
          <Label htmlFor="list-settings-name">Name</Label>
          <div className="flex items-center gap-2">
            <Input
              id="list-settings-name"
              value={name}
              aria-invalid={Boolean(nameError)}
              onChange={(event) => {
                setName(event.target.value)
                setNameError(undefined)
              }}
              onKeyDown={(event) => {
                if (event.key === `Enter` && nameChanged) bg(saveName())
              }}
            />
            {/* Nothing to save until the name differs from the saved one. */}
            <Button variant="secondary" loading={saving} disabled={!nameChanged} onClick={() => bg(saveName())}>
              Save
            </Button>
          </div>
          {nameError && <p className="text-body-xs text-red-600">{nameError}</p>}
        </div>
        {canChangeVisibility(list, user?.id) && (
          <SettingsRow title="Who can see it">
            <ListVisibilityField
              value={visibility}
              disabled={savingVisibility}
              onChange={(next) => bg(saveWhoCanSee(next))}
            />
          </SettingsRow>
        )}
        {editRule && (
          <SettingsRow title="Rule" help="Who is on this list.">
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                closeModal()
                openListRuleEditor({
                  list,
                  title: (
                    <ModalBackTitle
                      title="Edit rule"
                      backTo="Settings"
                      onBack={() => {
                        closeModal()
                        reopen()
                      }}
                    />
                  ),
                  onSaved: onChanged,
                  onDeleted: () => {
                    onChanged()
                    onDeleted()
                  },
                })
              }}
            >
              Edit rule
            </Button>
          </SettingsRow>
        )}
      </SettingsSection>
      {children}
      {/* The visibility picked here, not the one the dialog opened with, decides who may delete. */}
      {canDelete && (
        <SettingsSection>
          <SettingsRow title="Delete list" help="The people in it keep their records.">
            <Button variant="danger" size="sm" loading={deleting} onClick={() => bg(deleteList())}>
              Delete list
            </Button>
          </SettingsRow>
        </SettingsSection>
      )}
    </>
  )
}

function StagesRow({ list, exitStageNames }: { list: List; exitStageNames?: readonly string[] }): ReactElement {
  const { configuredStages, saveVisibility } = useStageVisibility(list)
  // An exit (Lost) is a drop zone, not a column, so there is nothing to show, hide or order.
  const stages = configuredStages.filter((stage) => !exitStageNames?.includes(stage.name))
  return (
    <SettingsRow title="Stages" help="Which stages your board shows, and in what order.">
      {/* Stages and cards open on top of Settings, so closing them is the way back. */}
      <EditStages
        stages={stages}
        onActionTriggered={saveVisibility}
        plain
        title={<ModalBackTitle title="Edit stages" backTo="Settings" onBack={closeModal} />}
      />
    </SettingsRow>
  )
}

function SystemStagesRow({ def }: { def: SystemListDef }): ReactElement | null {
  const { list } = useSystemList(def)
  if (!list) return null
  return (
    <SettingsSection title="Board">
      <StagesRow list={list} exitStageNames={def.exitStageNames} />
    </SettingsSection>
  )
}

function CardsRow(): ReactElement {
  const [rawCardConfig, setCardConfig] = useSettings<ListCardConfig>(LIST_CARD_CONFIG_SETTING)
  const cardConfig = getListCardConfig(rawCardConfig, isFieldEligible)

  const openCardCustomizer = () =>
    showModal(
      <PipelineCardCustomization
        initialConfig={cardConfig}
        onSave={(next) => setCardConfig(LIST_CARD_CONFIG_SETTING, next)}
        previewCard={({ contact, taskCounts, commContext, config }) => (
          <ListSlotCard
            contact={contact}
            config={config}
            taskCounts={taskCounts}
            commContext={commContext}
            dragging={false}
            onDragStart={() => {}}
            onDragEnd={() => {}}
            onOpen={() => {}}
          />
        )}
      />,
      {
        ...MODAL_OPTIONS,
        title: <ModalBackTitle title="Customize cards" backTo="Settings" onBack={closeModal} />,
      },
    )

  return (
    <SettingsRow title="Cards" help="Applies to all of your lists.">
      <Button variant="tertiary" size="sm" onClick={openCardCustomizer}>
        Customize cards
      </Button>
    </SettingsRow>
  )
}

const ColumnLinesRow = observer(function ColumnLinesRow(): ReactElement {
  const store = useUserSettingStore()
  // Absent until the setting's first read resolves; a save before then would overwrite a choice the person never saw.
  const loaded = HIDE_TABLE_COLUMN_LINES_SETTING in store.settings
  const hidden = store.settings[HIDE_TABLE_COLUMN_LINES_SETTING] === `true`
  return (
    <SettingsRow title="Column lines" help="Lines between table columns. Applies to every table.">
      <Switch
        aria-label="Column lines"
        checked={!hidden}
        disabled={!loaded}
        onCheckedChange={(on) => bg(async () => store.save(HIDE_TABLE_COLUMN_LINES_SETTING, JSON.stringify(!on)))}
      />
    </SettingsRow>
  )
})

/** Opens it in the app's modal, the way every other settings form here is opened. */
export function openListSettings(props: ListSettingsProps): void {
  showModal(<ListSettingsDialog {...props} />, {
    ...SETTINGS_MODAL_OPTIONS,
    title: props.target.source === `system` ? props.target.def.name : props.target.list.name || UNTITLED,
  })
}
